import { CheckIcon, ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons'
//src/components/ui/MultiSelectRadix/index.tsx
import * as Popover from '@radix-ui/react-popover'
import classnames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'

interface MultiSelectProps {
  ariaLabel: string
  placeholder: string
  options: string[]
  value: string[]
  onChange: (value: string[]) => void
  label?: string
  id?: string
  className?: string
  error?: string | null
  maxSelected?: number
}

const MultiSelectRadix = ({
  ariaLabel,
  placeholder,
  options,
  value,
  onChange,
  label,
  id,
  className,
  error,
  maxSelected = 3,
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(0)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (open && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus()
    }
  }, [focusedIndex, open])

  const toggleValue = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val))
    } else if (value.length < maxSelected) {
      onChange([...value, val])
    }
  }

  const removeValue = (val: string) => {
    onChange(value.filter((v) => v !== val))
  }

  const handleKeyDown = (e: React.KeyboardEvent, opt: string, index: number) => {
    const selected = value.includes(opt)
    const disabled = !selected && value.length >= maxSelected

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (!disabled) {
          toggleValue(opt)
        }
        break
      case 'ArrowDown':
        e.preventDefault()
        if (index < options.length - 1) {
          setFocusedIndex(index + 1)
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (index > 0) {
          setFocusedIndex(index - 1)
        }
        break
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        break
    }
  }

  return (
    <fieldset className={`${styles.fieldset} ${className || ''}`}>
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      )}

      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className={classnames(styles.Trigger, error ? styles.TriggerError : '')}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' && !open) {
                e.preventDefault()
                setOpen(true)
                setFocusedIndex(0)
              }
            }}
          >
            <div className={styles.TagContainer}>
              {value.length === 0 && <span className={styles.Placeholder}>{placeholder}</span>}
              {value.map((v) => (
                <span key={v} className={styles.Tag}>
                  {v}
                  {/* biome-ignore lint/a11y/useSemanticElements: Cannot use button inside trigger button, using span with role=button is appropriate here */}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      removeValue(v)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        removeValue(v)
                      }
                    }}
                    className={styles.TagRemove}
                    aria-label={`Remove ${v}`}
                  >
                    <Cross2Icon />
                  </span>
                </span>
              ))}
            </div>
            <ChevronDownIcon className={styles.Icon} />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content className={styles.Content} sideOffset={5} align="start" side="bottom">
            <div className={styles.Viewport}>
              {options.map((opt, index) => {
                const selected = value.includes(opt)
                const disabled = !selected && value.length >= maxSelected

                return (
                  <div
                    key={opt}
                    ref={(el) => {
                      itemRefs.current[index] = el
                    }}
                    className={classnames(styles.Item, {
                      [styles.ItemDisabled]: disabled,
                    })}
                    onClick={() => !disabled && toggleValue(opt)}
                    onKeyDown={(e) => handleKeyDown(e, opt, index)}
                    // biome-ignore lint/a11y/useSemanticElements: Custom multi-select requires div with role=option for proper ARIA listbox pattern
                    tabIndex={0}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={disabled}
                  >
                    <span>{opt}</span>
                    {selected && <CheckIcon className={styles.ItemIndicator} />}
                  </div>
                )
              })}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {error && <p className={styles.errorText}>{error}</p>}
    </fieldset>
  )
}

export default MultiSelectRadix
